import ActionSuccessToast from '../components/ActionSuccessToast'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, ChevronDown, ChevronUp, Upload, Trash2, X, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useBiometricGate } from '../hooks/useBiometricGate'
import { fmt } from '../lib/calculations'
import { isOCCSymbol, formatOCC, parseOCC } from '../lib/occParser'
import { cancelOpenOrdersForSymbol } from '../lib/cancelOpenOrdersForSymbol'
import PrivateValue from '../components/PrivateValue'
import SetupImportModal from '../components/SetupImportModal'
import { derivePositionPnl } from '../lib/broker/normalizedPnl'
import { computeEquityLotAccounting } from '../lib/positionLotAccounting'
import { ibkrQuoteStream, selectPositionQuotePrice } from '../lib/marketData/ibkrQuoteSubscriptionStore.js'
import { getMarketSession } from '../lib/marketSession'
import { resolvePositionListStage, shouldShowPositionListProject } from '../lib/positionListStage'
import { buildPositionWorkspaceTarget } from '../lib/positionWorkspaceRoute'

/* ── Constants ─────────────────────────────────────────────────────────────── */

const STAGES = [
  { id: 'idea',     label: 'IDEA',     accent: 'text-inkMuted',   bg: 'bg-canvas2/50',    border: 'border-border' },
  { id: 'working',  label: 'WORKING',  accent: 'text-inkMuted',   bg: 'bg-canvas2/50',    border: 'border-border' },
  { id: 'active',   label: 'ACTIVE',   accent: 'text-forest',     bg: 'bg-forest/5',      border: 'border-forest/20' },
  { id: 'closed',   label: 'CLOSED',   accent: 'text-inkMuted',   bg: 'bg-canvas2/50',    border: 'border-border' },
]

const STAGE_BADGE = {
  idea:     'bg-canvas2 text-inkMuted border-border',
  working:  'bg-canvas2 text-inkMuted border-border',
  active:   'bg-forest/10 text-forest border-forest/20',
  closed:   'bg-canvas2 text-inkMuted border-border',
}

const ASSET_STAGE_PRIORITY = { active: 4, working: 3, idea: 2, planned: 1 }

function assetStageRank(stage) {
  return ASSET_STAGE_PRIORITY[String(stage || '').toLowerCase()] || 0
}

/* ── Helpers ───────────────────────────────────────────────────────────────── */

function fmtCurrency(val) {
  if (val == null) return '—'
  return Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtPct(val) {
  if (val == null || !isFinite(val)) return '—'
  return (val >= 0 ? '+' : '') + val.toFixed(2) + '%'
}

function clr(val) {
  if (val > 0) return 'text-forest'
  if (val < 0) return 'text-ember'
  return 'text-inkMuted'
}

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

function fmtDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}

/* ── Grouping Logic ───────────────────────────────────────────────────────── */

/**
 * Groups every current lifecycle row into one asset card at the symbol's
 * highest stage. Closed history keeps its campaign identity.
 *
 * Returns an array of "grouped card" objects per stage.
 */
function groupProjectsBySymbol(projects, livePositionMap, closedTradeMap, executionMap = {}, liveTickMap = {}, marketSession = 'closed') {
  const groups = {}

  for (const p of projects) {
    const symbolKey = String(p.symbol || '').toUpperCase()
    const liveRows = livePositionMap?.[symbolKey] || []
    const displayStage = resolvePositionListStage(p, {
      hasLivePosition: liveRows.some(row => Math.abs(Number(row.quantity) || 0) > 0),
      hasLinkedTrade: Boolean(p._trade),
    })
    const isClosed = displayStage === 'closed' || displayStage === 'reviewed'
    const groupIdentity = isClosed
      ? (p.created_at ? new Date(p.created_at).toISOString().slice(0, 19) : 'none')
      : 'current'
    const key = `${p.symbol}::${groupIdentity}`

    if (!groups[key]) {
      groups[key] = {
        symbol: p.symbol,
        stage: displayStage,
        created_at: p.created_at,
        updated_at: p.updated_at,
        setup_type: p.setup_type,
        is_draft: p.is_draft,
        // Consolidated data
        projectIds: [],
        projects: [],
        trades: [],
        primaryProject: p,
      }
    } else if (assetStageRank(displayStage) > assetStageRank(groups[key].stage)) {
      groups[key].stage = displayStage
      groups[key].primaryProject = p
      groups[key].created_at = p.created_at
      groups[key].setup_type = p.setup_type
      groups[key].is_draft = p.is_draft
    }

    groups[key].projectIds.push(p.id)
    groups[key].projects.push(p)
    if (p._trade) groups[key].trades.push(p._trade)
    // Keep setup_type from first project that has one
    if (!groups[key].setup_type && p.setup_type) groups[key].setup_type = p.setup_type
    // Keep most recent updated_at
    if (p.updated_at && (!groups[key].updated_at || p.updated_at > groups[key].updated_at)) {
      groups[key].updated_at = p.updated_at
    }
  }

  // Build consolidated card objects
  return Object.values(groups).map(g => {
    // Collect all account_sizing entries across grouped projects
    const allSizing = g.projects.flatMap(p => p.account_sizing || [])

    const primaryProject = g.primaryProject || g.projects[0]

    // Active campaigns can consolidate account rows. Alternative pre-fill
    // setups remain independent, so the asset card shows its leading setup.
    const entryPrices = g.projects.map(p => p.entry_price).filter(v => v != null)
    let entryPrice = null
    const onlyActiveRows = g.stage === 'active' && g.projects.every(p => p.stage === 'active')
    if (!onlyActiveRows) {
      entryPrice = primaryProject?.entry_price != null ? Number(primaryProject.entry_price) : null
    } else if (entryPrices.length > 0) {
      const unique = [...new Set(entryPrices.map(Number))]
      if (unique.length === 1) {
        entryPrice = unique[0]
      } else {
        // Weighted average by shares
        let totalWeightedEntry = 0
        let totalEntryShares = 0
        for (const p of g.projects) {
          if (p.entry_price != null) {
            const pShares = (p.account_sizing || []).reduce((s, a) => s + (Number(a.shares) || 0), 0)
            totalWeightedEntry += Number(p.entry_price) * pShares
            totalEntryShares += pShares
          }
        }
        entryPrice = totalEntryShares > 0 ? totalWeightedEntry / totalEntryShares : unique[0]
      }
    }

    // Stop and target from first project (should be same across grouped)
    const stopPrice = primaryProject?.stop_price ?? null
    const targetPrice = primaryProject?.target_price ?? null

    // Total max loss from sizing
    const totalMaxLoss = allSizing.reduce((sum, s) => sum + (Number(s.max_loss) || 0), 0)

    const isActive = g.stage === 'active'
    const isClosed = g.stage === 'closed' || g.stage === 'reviewed'

    // ── ACTIVE enrichment from schwab_positions ──
    let accountCount = allSizing.length
    let totalShares = allSizing.reduce((sum, s) => sum + (Number(s.shares) || 0), 0)
    let liveUnrealizedPnl = null
    let liveCurrentPrice = null
    let liveMktVal = null
    let isOption = false
    let livePnlLabel = 'open'

    if (isActive && livePositionMap) {
      const liveRows = livePositionMap[g.symbol.toUpperCase()] || []
      if (liveRows.length > 0) {
        let totalQty = 0, totalMktValCalc = 0, totalCostBasis = 0
        const uniqueAccounts = new Set()

        for (const pos of liveRows) {
          const qty = Number(pos.quantity) || 0
          const mktVal = Number(pos.market_value) || 0
          // EF-156: Single source of truth for cost basis. Reads normalized row.
          const { costBasis } = derivePositionPnl(pos)

          totalQty += qty
          totalMktValCalc += mktVal
          totalCostBasis += costBasis
          uniqueAccounts.add(pos.account_number)
        }

        totalShares = totalQty
        accountCount = uniqueAccounts.size
        // For options: market_value is already ×100, so divide by qty AND 100 to get per-share price
        isOption = liveRows.some(p => p.instrument_type === 'OPTION')
        const priceDivisor = isOption ? totalQty * 100 : totalQty
        const schwabPrice = totalQty > 0 ? totalMktValCalc / priceDivisor : null
        const liveTick = liveTickMap[g.symbol.toUpperCase()] || {}
        liveCurrentPrice = selectPositionQuotePrice(liveTick, liveTick.session || marketSession, isOption ? 'OPT' : 'STK') ?? schwabPrice
        liveMktVal = liveCurrentPrice != null && totalQty > 0
          ? liveCurrentPrice * priceDivisor
          : totalMktValCalc

        const lotAccounting = !isOption
          ? computeEquityLotAccounting(executionMap[g.symbol.toUpperCase()] || [], {
              symbol: g.symbol,
              accountNumbers: [...uniqueAccounts],
              markPrice: liveCurrentPrice,
              method: 'LIFO',
            })
          : null
        const lotQtyMatchesBroker = lotAccounting?.available
          && lotAccounting.unmatchedExitQty === 0
          && Math.abs(Number(lotAccounting.remainingQty) - Math.abs(totalQty)) < 0.0001
        liveUnrealizedPnl = lotQtyMatchesBroker
          ? lotAccounting.openPnl
          : liveMktVal - totalCostBasis
      }
    }

    // ── CLOSED enrichment from trades table ──
    let totalPnl = g.trades.length > 0
      ? g.trades.reduce((sum, t) => sum + (Number(t.net_pnl) || 0), 0)
      : null
    let totalTradeQty = g.trades.length > 0
      ? g.trades.reduce((sum, t) => sum + (Number(t.quantity_total) || 0), 0)
      : null
    let closedTradeData = null

    if (isClosed && closedTradeMap) {
      // Collect all linked trades for this group's projects
      const linkedTrades = g.projects
        .map(p => p.trade_id && closedTradeMap[p.trade_id])
        .filter(Boolean)

      if (linkedTrades.length > 0) {
        const sumPnl = linkedTrades.reduce((s, t) => s + (Number(t.net_pnl) || 0), 0)
        const sumRoi = linkedTrades.length === 1
          ? Number(linkedTrades[0].pnl_percent) || 0
          : null // For multiple trades, ROI isn't simply summable
        const firstTrade = linkedTrades[0]
        const lastTrade = linkedTrades[linkedTrades.length - 1]

        totalPnl = sumPnl

        closedTradeData = {
          netPnl: sumPnl,
          netRoi: sumRoi,
          entryPrice: Number(firstTrade.avg_entry_price) || null,
          exitPrice: Number(firstTrade.avg_exit_price) || null,
          entryDate: firstTrade.entry_date,
          exitDate: lastTrade.exit_date || firstTrade.exit_date,
        }
      }
    }

    // Setup metadata from first project (AGI tracker data)
    const firstProject = primaryProject
    const setupPattern = firstProject?.setup_pattern || null
    const entryType = firstProject?.entry_type || null
    const triggerPrice = firstProject?.trigger_price != null ? Number(firstProject.trigger_price) : null
    const patternFailurePrice = firstProject?.pattern_failure_price != null ? Number(firstProject.pattern_failure_price) : null
    const sellTargetPrice = firstProject?.sell_target_price != null ? Number(firstProject.sell_target_price) : null
    const direction = firstProject?.direction || 'LONG'
    const setupSource = firstProject?.setup_source || null

    return {
      // Identity
      id: firstProject?.id || g.projectIds[0],
      projectIds: g.projectIds,
      symbol: g.symbol,
      stage: g.stage,
      setup_type: g.setup_type,
      is_draft: g.is_draft,
      created_at: g.created_at,
      updated_at: g.updated_at,
      // Consolidated
      entryPrice,
      stopPrice,
      targetPrice,
      accountCount,
      totalShares,
      totalMaxLoss,
      totalPnl,
      totalTradeQty,
      hasTrades: g.trades.length > 0,
      isGrouped: g.projectIds.length > 1,
      // Live ACTIVE data
      liveUnrealizedPnl,
      livePnlLabel,
      liveCurrentPrice,
      liveMktVal,
      isOption,
      // Closed trade data
      closedTradeData,
      // Setup metadata (AGI tracker)
      setupPattern,
      entryType,
      triggerPrice,
      patternFailurePrice,
      sellTargetPrice,
      direction,
      setupSource,
    }
  })
}

/* ── Position Card ─────────────────────────────────────────────────────────── */

function PositionCard({ card, onClick, onDeleteCard, onCancelWorking }) {
  const [confirmAction, setConfirmAction] = useState(null) // 'delete' | 'cancel' | null
  const [cancelling, setCancelling] = useState(false)
  const stage = card.stage || 'idea'
  const badgeCls = STAGE_BADGE[stage] || STAGE_BADGE.idea

  const isActive = stage === 'active'
  const isClosed = stage === 'closed' || stage === 'reviewed'
  const isIdea = stage === 'idea'
  const isWorking = stage === 'working'
  const hasSetup = !!card.setupPattern

  // Determine P&L: live unrealized for ACTIVE, realized from trades for CLOSED
  const pnl = isActive && card.liveUnrealizedPnl != null
    ? card.liveUnrealizedPnl
    : card.totalPnl
  const isWin = pnl != null && pnl > 0
  const isLoss = pnl != null && pnl < 0

  const ct = card.closedTradeData

  const handleCancelConfirm = async () => {
    setCancelling(true)
    setConfirmAction(null)
    await onCancelWorking(card)
    setCancelling(false)
  }

  // Inline confirmation (delete or cancel orders)
  if (confirmAction === 'delete') {
    return (
      <div className="w-full text-left bg-canvas border border-ember/30 rounded-xl p-4">
        <div className="font-mono text-xs text-ink mb-3">
          This will attempt to cancel any working Schwab orders for{' '}
          <span className="font-black">{card.symbol}</span> AND permanently remove the Erevis setup card. Continue?
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { onDeleteCard(card); setConfirmAction(null) }}
            className="font-mono text-[10px] font-semibold px-3 py-1.5 rounded-lg border border-ember bg-ember/10 text-ember hover:bg-ember/20 transition-colors"
          >
            Cancel & Delete
          </button>
          <button
            onClick={() => setConfirmAction(null)}
            className="font-mono text-[10px] px-3 py-1.5 rounded-lg border border-border text-inkMuted hover:text-ink transition-colors"
          >
            Keep
          </button>
        </div>
      </div>
    )
  }

  if (confirmAction === 'cancel') {
    return (
      <div className="w-full text-left bg-canvas border border-ember/30 rounded-xl p-4">
        <div className="font-mono text-xs text-ink mb-3">
          Cancel orders for <span className="font-black">{card.symbol}</span>?
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCancelConfirm}
            className="font-mono text-[10px] font-semibold px-3 py-1.5 rounded-lg border border-ember bg-ember/10 text-ember hover:bg-ember/20 transition-colors"
          >
            Cancel Orders
          </button>
          <button
            onClick={() => setConfirmAction(null)}
            className="font-mono text-[10px] px-3 py-1.5 rounded-lg border border-border text-inkMuted hover:text-ink transition-colors"
          >
            Keep
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      onClick={onClick}
      className="w-full text-left bg-canvas border border-border rounded-xl p-4 hover:border-ink/40 hover:bg-canvas2/20 transition-all cursor-pointer group relative"
    >
      {/* X (Cancel Orders) — WORKING cards only. */}
      {isWorking && !cancelling && (
        <button
          data-testid="cancel-working-btn"
          onClick={(e) => { e.stopPropagation(); setConfirmAction('cancel') }}
          className="absolute top-2.5 right-2.5 p-1 rounded text-inkDim hover:text-ember hover:bg-ember/10 opacity-0 group-hover:opacity-100 transition-all"
          title="Cancel orders"
        >
          <X size={13} />
        </button>
      )}

      {/* Spinner while cancelling (X button path only) */}
      {isWorking && cancelling && (
        <div className="absolute top-2.5 right-2.5 p-1 z-10">
          <Loader2 size={13} className="text-inkMuted animate-spin" />
        </div>
      )}

      {/* Trashcan (Cancel & Delete) — IDEA cards only. EF-177 keeps the
          cancel-before-delete handler; EF-178a removed WORKING-card
          visibility because the Position Board is not where Brandon
          closes positions. */}
      {isIdea && (
        <button
          data-testid="delete-card-btn"
          onClick={(e) => { e.stopPropagation(); setConfirmAction('delete') }}
          className="absolute top-2.5 right-2.5 p-1 rounded text-inkDim hover:text-ember hover:bg-ember/10 opacity-0 group-hover:opacity-100 transition-all"
          title="Cancel & delete"
        >
          <Trash2 size={13} />
        </button>
      )}

      {/* Row 1: Symbol + badges */}
      <div className="flex items-center gap-2 mb-2 flex-wrap pr-6">
        <span className="font-black text-base uppercase tracking-wide text-ink">{isOCCSymbol(card.symbol) ? formatOCC(card.symbol) : card.symbol}</span>
        <span className={`font-mono text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-widest ${badgeCls}`}>
          {stage}
        </span>
        {/* Direction badge for ideas with setup data */}
        {hasSetup && (
          <span className={`font-mono text-[9px] px-2 py-0.5 rounded font-bold tracking-widest ${
            card.direction === 'SHORT' ? 'bg-ember/10 text-ember border border-ember/30' : 'bg-forest/10 text-forest border border-forest/30'
          }`}>
            {card.direction}
          </span>
        )}
        {card.is_draft && (
          <span className="font-mono text-[9px] px-2 py-0.5 rounded-full border bg-ember/10 text-ember border-ember/20 uppercase tracking-widest">
            Draft
          </span>
        )}
        {isClosed && pnl != null && (
          <span className={`font-mono text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-widest font-semibold ${
            isWin ? 'bg-forest/10 text-forest border-forest/30' : isLoss ? 'bg-ember/10 text-ember border-ember/30' : 'bg-canvas2 text-inkMuted border-border'
          }`}>
            {isWin ? 'WIN' : isLoss ? 'LOSS' : 'BE'}
          </span>
        )}
        {!hasSetup && card.setup_type && (
          <span className="font-mono text-[9px] px-2 py-0.5 rounded-full border bg-canvas2 text-inkMuted border-border">
            {card.setup_type}
          </span>
        )}
      </div>

      {/* Ideas with setup: pattern + entry type */}
      {isIdea && hasSetup && (
        <>
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="font-mono text-[11px] text-ink font-medium">{card.setupPattern}</span>
            {card.entryType && (
              <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                card.entryType === 'On Strength'
                  ? 'bg-forest/10 text-forest border border-forest/30'
                  : 'bg-[#c4953a]/10 text-[#c4953a] border border-[#c4953a]/30'
              }`}>
                {card.entryType}
              </span>
            )}
          </div>
          {/* Trigger → Target range */}
          {card.triggerPrice != null && card.sellTargetPrice != null && (
            <div className="mb-1.5">
              <PrivateValue>
                <span className="font-mono text-xs text-ink">{fmtCurrency(card.triggerPrice)}</span>
                <span className="font-mono text-[10px] text-inkMuted mx-1">{'\u2192'}</span>
                <span className="font-mono text-xs text-forest">{fmtCurrency(card.sellTargetPrice)}</span>
              </PrivateValue>
            </div>
          )}
        </>
      )}

      {/* Row 2: Current price (ACTIVE with live data) or Entry → Exit (CLOSED) */}
      {isActive && card.liveCurrentPrice != null && (
        <div className="mb-1.5">
          <span className="font-mono text-[9px] text-inkMuted uppercase tracking-wider mr-2">Price</span>
          <span className="font-mono text-xs text-ink">{fmtCurrency(card.liveCurrentPrice)}</span>
        </div>
      )}
      {isClosed && ct && ct.entryPrice != null && ct.exitPrice != null ? (
        <div className="mb-1.5">
          <span className="font-mono text-[9px] text-inkMuted uppercase tracking-wider mr-2">Entry {'\u2192'} Exit</span>
          <PrivateValue>
            <span className="font-mono text-xs text-ink">{fmtCurrency(ct.entryPrice)} {'\u2192'} {fmtCurrency(ct.exitPrice)}</span>
          </PrivateValue>
        </div>
      ) : !hasSetup && card.entryPrice != null && (
        <div className="mb-1.5">
          <span className="font-mono text-[9px] text-inkMuted uppercase tracking-wider mr-2">Entry</span>
          <PrivateValue><span className="font-mono text-xs text-ink">{fmt(card.entryPrice)}</span></PrivateValue>
        </div>
      )}

      {/* Row 3: Shares + accounts */}
      <div className="flex items-center gap-3 mb-1.5">
        {card.totalShares > 0 && (
          <span className="font-mono text-[10px] text-inkMuted">{Math.round(card.totalShares)} {card.isOption ? 'contracts' : 'shares'}</span>
        )}
        {card.accountCount > 0 && (
          <span className="font-mono text-[10px] text-inkMuted">
            {card.accountCount} account{card.accountCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Row 4: P&L */}
      {isActive && card.liveUnrealizedPnl != null && (
        <div className={`font-mono text-xs font-semibold mb-1.5 ${clr(card.liveUnrealizedPnl)}`}>
          <PrivateValue>{(card.liveUnrealizedPnl >= 0 ? '+' : '') + fmtCurrency(card.liveUnrealizedPnl)}</PrivateValue>
          <span className="font-mono text-[9px] text-inkMuted ml-1">{card.livePnlLabel || 'open'}</span>
        </div>
      )}
      {isClosed && pnl != null && (
        <div className={`font-mono text-xs font-semibold mb-1.5 ${clr(pnl)}`}>
          <PrivateValue>
            {(pnl >= 0 ? '+' : '') + fmtCurrency(pnl)}
            {ct && ct.netRoi != null && (
              <span className="text-[10px] ml-1">({fmtPct(ct.netRoi)})</span>
            )}
          </PrivateValue>
          <span className="font-mono text-[9px] text-inkMuted ml-1">realized</span>
        </div>
      )}
      {/* Fallback: non-live active or pre-close with trades */}
      {!isActive && !isClosed && pnl != null && (
        <div className={`font-mono text-xs font-semibold mb-1.5 ${clr(pnl)}`}>
          <PrivateValue>{(pnl >= 0 ? '+' : '') + fmtCurrency(pnl)}</PrivateValue>
        </div>
      )}
      {pnl == null && !hasSetup && card.totalMaxLoss !== 0 && card.totalMaxLoss != null && (
        <div className="font-mono text-[10px] text-ember mb-1.5">
          <PrivateValue>Max loss: {fmtCurrency(card.totalMaxLoss)}</PrivateValue>
        </div>
      )}

      {/* CLOSED: Date range */}
      {isClosed && ct && ct.exitDate && (
        <div className="font-mono text-[10px] text-inkDim mb-1">
          {ct.entryDate ? `${fmtDate(ct.entryDate)} ${'\u2192'} ${fmtDate(ct.exitDate)}` : `Closed ${fmtDate(ct.exitDate)}`}
        </div>
      )}

      {/* Bottom: time + source */}
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] text-inkDim">{timeAgo(card.created_at)}</span>
        {hasSetup && card.setupSource && (
          <span className="font-mono text-[9px] text-inkDim">{'\u00b7'} {card.setupSource}</span>
        )}
      </div>
    </div>
  )
}

/* ── Stage Section ─────────────────────────────────────────────────────────── */

function StageSection({ stage, cards, onCardClick, onDeleteCard, onCancelWorking, collapsible }) {
  const [collapsed, setCollapsed] = useState(false)
  const count = cards.length

  return (
    <div className="min-w-0">
      <button
        onClick={collapsible ? () => setCollapsed(!collapsed) : undefined}
        className={`flex items-center gap-2 mb-3 w-full ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className={`w-2 h-2 rounded-full ${
          stage.id === 'active' ? 'bg-forest' : 'bg-inkDim'
        }`} />
        <span className={`font-mono text-[10px] tracking-widest uppercase font-semibold ${stage.accent}`}>
          {stage.label}
        </span>
        <span className="font-mono text-[10px] text-inkDim bg-canvas2 border border-border rounded-full px-2 py-0.5">
          {count}
        </span>
        {collapsible && (
          collapsed
            ? <ChevronDown size={12} className="text-inkMuted ml-auto" />
            : <ChevronUp size={12} className="text-inkMuted ml-auto" />
        )}
      </button>

      {!collapsed && (
        <>
          {cards.length === 0 && (
            <div className="font-mono text-[10px] text-inkDim py-4 text-center border border-dashed border-border rounded-lg">
              No positions
            </div>
          )}
          {cards.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {cards.map(c => (
                <PositionCard key={c.id} card={c} onClick={() => onCardClick(c)} onDeleteCard={onDeleteCard} onCancelWorking={onCancelWorking} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/* ── Main Component ────────────────────────────────────────────────────────── */

export default function PositionBoard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const biometricGate = useBiometricGate()

  const [projects, setProjects] = useState([])
  const [livePositionMap, setLivePositionMap] = useState(null)
  const [liveTickMap, setLiveTickMap] = useState({})
  const [marketSession, setMarketSession] = useState(() => getMarketSession())
  const [activeExecutionMap, setActiveExecutionMap] = useState({})
  const [closedTradeMap, setClosedTradeMap] = useState(null)
  const [loading, setLoading] = useState(true)
  const [importOpen, setImportOpen] = useState(false)
  // EF-177: surfaced after the trashcan path so partial broker-cancel
  // failures don't disappear silently. { tone: 'success'|'error', text }.
  const [notice, setNotice] = useState(null)

  useEffect(() => {
    const timer = setInterval(() => setMarketSession(getMarketSession()), 60_000)
    return () => clearInterval(timer)
  }, [])

  const loadProjects = useCallback(async () => {
    if (!user) return
    if (projects.length === 0) setLoading(true)
    const { data } = await supabase
      .from('position_projects')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_draft', false)
      .order('created_at', { ascending: false })

    const fetched = data || []

    // ── Fetch ALL open schwab_positions for live ACTIVE enrichment ──
    const { data: livePositions } = await supabase
      .from('schwab_positions')
      .select('symbol, quantity, market_value, average_price, current_day_pnl, account_number, status, instrument_type, cost_basis_long, cost_basis_short, unrealized_pnl_long, unrealized_pnl_short, cost_basis_source, currency, contract_multiplier, last_updated_at')
      .eq('user_id', user.id)

    const openPositions = (livePositions || []).filter(p => p.status === 'open' || !p.status)

    // Group by uppercase symbol
    const posMap = {}
    for (const pos of openPositions) {
      const sym = (pos.symbol || '').toUpperCase()
      if (!posMap[sym]) posMap[sym] = []
      posMap[sym].push(pos)
    }
    setLivePositionMap(posMap)

    // ── Fetch linked trades for ALL projects (basic fields for grouping) ──
    const tradeIds = fetched.map(p => p.trade_id).filter(Boolean)
    let tradeMap = {}
    if (tradeIds.length > 0) {
      const { data: trades } = await supabase
        .from('trades')
        .select('id, net_pnl, pnl_percent, quantity_total, status, avg_entry_price')
        .in('id', tradeIds)
      tradeMap = Object.fromEntries((trades || []).map(t => [t.id, t]))
    }

    // ── Fetch richer trade data for CLOSED projects ──
    const closedProjects = fetched.filter(p => p.stage === 'closed' || p.stage === 'reviewed')
    const closedTradeIds = closedProjects.map(p => p.trade_id).filter(Boolean)
    let closedMap = {}
    if (closedTradeIds.length > 0) {
      const { data: closedTrades } = await supabase
        .from('trades')
        .select('id, symbol, net_pnl, pnl_percent, avg_entry_price, avg_exit_price, entry_date, exit_date, quantity_total, status, account_number')
        .in('id', closedTradeIds)
      closedMap = Object.fromEntries((closedTrades || []).map(t => [t.id, t]))
    }
    setClosedTradeMap(closedMap)

    // ── Fetch executions for active positions so board-card open P&L mirrors
    // PositionHeader's reconciled lot-accounting path.
    const activeSymbols = [...new Set(fetched
      .filter(p => p.stage === 'active')
      .map(p => String(p.symbol || '').toUpperCase())
      .filter(Boolean))]
    let executionMap = {}
    if (activeSymbols.length > 0) {
      const { data: activeTrades } = await supabase
        .from('trades')
        .select('id, symbol, status')
        .eq('user_id', user.id)

      const activeTradeRows = (activeTrades || []).filter(t =>
        activeSymbols.includes(String(t.symbol || '').toUpperCase())
        && (t.status === 'open' || t.status === 'partial')
      )
      const activeTradeIds = activeTradeRows.map(t => t.id).filter(Boolean)

      if (activeTradeIds.length > 0) {
        const { data: executions } = await supabase
          .from('executions')
          .select('trade_id, account_number, symbol, execution_type, side, quantity, price, commission, fees, executed_at')
          .in('trade_id', activeTradeIds)

        for (const exec of executions || []) {
          const sym = String(exec.symbol || '').toUpperCase()
          if (!sym) continue
          if (!executionMap[sym]) executionMap[sym] = []
          executionMap[sym].push(exec)
        }
      }
    }

    // Attach trade data to projects
    const enriched = fetched.map(p => ({
      ...p,
      _trade: p.trade_id ? tradeMap[p.trade_id] || null : null,
    }))
    const visibleEnriched = enriched.filter((p) => {
      const symbolKey = String(p.symbol || '').toUpperCase()
      const liveRows = posMap[symbolKey] || []
      return shouldShowPositionListProject(p, {
        hasLivePosition: liveRows.some(row => Math.abs(Number(row.quantity) || 0) > 0),
        hasLinkedTrade: Boolean(p._trade),
      })
    })

    setProjects(visibleEnriched)
    setActiveExecutionMap(executionMap)
    setLoading(false)
  }, [user])

  useEffect(() => { loadProjects() }, [loadProjects])

  const activeSymbols = useMemo(() => [
    ...new Set(projects
      .filter(p => p.stage === 'active')
      .map(p => String(p.symbol || '').toUpperCase())
      .filter(Boolean)),
  ], [projects])

  useEffect(() => {
    setLiveTickMap({})
    if (activeSymbols.length === 0) return
    let cancelled = false
    const unsubscribers = []

    for (const symbol of activeSymbols) {
      const occ = isOCCSymbol(symbol) ? parseOCC(symbol) : null
      const streamSymbol = occ ? occ.underlying : symbol
      const params = occ
        ? { secType: 'OPT', expiry: occ.expiry, strike: String(occ.strike), right: occ.right }
        : {}

      ibkrQuoteStream.subscribe(streamSymbol, (tick) => {
        if (tick?.field === 'reset') {
          setLiveTickMap(prev => ({ ...prev, [symbol]: {} }))
          return
        }
        if (cancelled || !['last', 'close', 'sessionClose', 'lastRth', 'bid', 'ask'].includes(tick?.field)) return
        const value = Number(tick.value)
        if (!(value > 0)) return
        setLiveTickMap(prev => ({
          ...prev,
          [symbol]: {
            ...(prev[symbol] || {}),
            [tick.field]: value,
            ...(tick.session ? { session: tick.session } : {}),
            ...(tick.contract ? { contract: tick.contract } : {}),
          },
        }))
      }, params)
        .then((unsubscribe) => {
          if (cancelled) {
            unsubscribe?.()
            return
          }
          unsubscribers.push(unsubscribe)
        })
        .catch(() => {})
    }

    return () => {
      cancelled = true
      for (const unsubscribe of unsubscribers) unsubscribe?.()
    }
  }, [activeSymbols])

  // EF-177: auto-dismiss success notice after 12s. Errors persist until
  // explicitly dismissed so a partial-cancel failure can't be missed.
  useEffect(() => {
    if (!notice || notice.tone !== 'success') return
    const t = setTimeout(() => setNotice(null), 12000)
    return () => clearTimeout(t)
  }, [notice])

  // Group projects by symbol + created_at timestamp, then bucket by stage
  const grouped = useMemo(() => {
    const allCards = groupProjectsBySymbol(projects, livePositionMap, closedTradeMap, activeExecutionMap, liveTickMap, marketSession)
    return STAGES.map(s => ({
      stage: s,
      cards: allCards
        .filter(c => c.stage === s.id)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    }))
  }, [projects, livePositionMap, closedTradeMap, activeExecutionMap, liveTickMap, marketSession])

  // EF-177: trashcan path on IDEA + WORKING cards. Always cancels any open
  // Schwab orders for the symbol BEFORE deleting the row, then surfaces a
  // notice for partial failures. The Supabase delete runs unconditionally so
  // a partial cancel-failure cannot leave a worse state where Erevis no
  // longer knows about live orders that are still at the broker.
  const handleDeleteCard = useCallback(async (card) => {
    if (!user || !card?.projectIds?.length) return

    // Biometric verification — same pattern as handleCancelWorking.
    // FA-01-passkey-hardening (D2): surface the specific error from the
    // gate so users without a registered passkey are told why the
    // delete didn't fire, rather than seeing the trashcan silently no-op.
    let brokerWriteProof = null
    try {
      const { verified, error: bioError, brokerWriteProof: proof } = await biometricGate.verify()
      brokerWriteProof = proof || null
      if (!verified) {
        setNotice({ tone: 'error', text: bioError || 'Biometric verification failed' })
        return
      }
    } catch { return }

    // Resolve a fresh session for the broker calls.
    let session = null
    try {
      const { data } = await supabase.auth.getSession()
      session = data?.session || null
    } catch { /* fall through — cancelOpenOrdersForSymbol will report */ }

    const cancelResult = await cancelOpenOrdersForSymbol(card.symbol, session || {}, { brokerWriteProof })

    // Always proceed with the row delete, regardless of cancel outcome.
    const idsSet = new Set(card.projectIds)
    setProjects(prev => prev.filter(p => !idsSet.has(p.id)))
    await supabase
      .from('position_projects')
      .delete()
      .eq('user_id', user.id)
      .in('id', card.projectIds)

    loadProjects()

    // Surface a notice based on the cancel outcome.
    const { attempted, successCount, failureCount } = cancelResult
    if (attempted === 0) {
      setNotice(null)
    } else if (failureCount === 0) {
      setNotice({
        tone: 'success',
        text: `Canceled ${successCount} working order(s) at Schwab and removed the ${card.symbol} setup.`,
      })
    } else if (successCount === 0) {
      setNotice({
        tone: 'error',
        text: `Could not cancel ${attempted} working order(s) at Schwab for ${card.symbol}. ALL must be canceled manually in ThinkorSwim. The Erevis setup card has been removed.`,
      })
    } else {
      setNotice({
        tone: 'error',
        text: `Canceled ${successCount} of ${attempted} orders for ${card.symbol}. ${failureCount} order(s) MUST be canceled manually in ThinkorSwim. The Erevis setup card has been removed.`,
      })
    }
  }, [user, biometricGate, loadProjects])

  const handleCancelWorking = useCallback(async (card) => {
    if (!user || !card?.projectIds?.length) return

    // Biometric verification
    // FA-01-passkey-hardening (D2): surface error so no-passkey users see why.
    let brokerWriteProof = null
    try {
      const { verified, error: bioError, brokerWriteProof: proof } = await biometricGate.verify()
      brokerWriteProof = proof || null
      if (!verified) {
        setNotice({ tone: 'error', text: bioError || 'Biometric verification failed' })
        return
      }
    } catch { return }

    // Optimistic: remove from working column
    const idsSet = new Set(card.projectIds)
    setProjects(prev => prev.map(p =>
      idsSet.has(p.id) ? { ...p, stage: 'idea', setup_status: 'idea' } : p
    ))

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      // Fetch working orders from Schwab
      const ordersRes = await fetch('/api/schwab/orders', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      const ordersData = ordersRes.ok ? await ordersRes.json() : { orders: [] }
      const allOrders = ordersData.orders || []

      // Filter orders for this symbol — cancel TRIGGER parents (children auto-cancel)
      const upperSymbol = card.symbol.toUpperCase()
      const toCancel = []
      for (const order of allOrders) {
        const sym = order.orderLegCollection?.[0]?.instrument?.symbol?.toUpperCase()
        if (sym !== upperSymbol) continue
        const status = order.status?.toUpperCase()
        if (['FILLED', 'CANCELED', 'EXPIRED', 'REJECTED', 'REPLACED'].includes(status)) continue
        toCancel.push({
          account_number: order.accountNumber,
          symbol: upperSymbol,
          existingOrderId: order.orderId,
        })
      }

      // Send cancels through OMS
      if (toCancel.length > 0) {
        await fetch('/api/oms/execute', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(
            brokerWriteProof
              ? { action: 'CANCEL', orders: toCancel, brokerWriteProof }
              : { action: 'CANCEL', orders: toCancel },
          ),
        })
      }

      // Revert position_projects to idea stage
      await supabase
        .from('position_projects')
        .update({ stage: 'idea', setup_status: 'idea', updated_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .in('id', card.projectIds)

    } catch (err) {
      console.error('[PositionBoard] Cancel working error:', err.message)
    }

    loadProjects()
  }, [user, biometricGate, loadProjects])

  const handleCardClick = (card) => {
    navigate(buildPositionWorkspaceTarget({ symbol: card.symbol, projectIds: card.projectIds }))
  }

  if (loading) {
    return <div className="font-mono text-xs text-inkMuted animate-pulse">Loading...</div>
  }

  const totalCards = grouped.reduce((sum, g) => sum + g.cards.length, 0)

  return (
    <div className="space-y-6">
      {/* EF-177 notice banner — surfaces broker-cancel outcomes from the
          trashcan path. Green tones auto-dismiss; red tones persist until
          the user dismisses, so partial-cancel failures cannot be missed. */}
      <ActionSuccessToast message={notice?.tone === 'success' ? notice.text : null} onDismiss={() => setNotice(null)} testId="position-board-notice" />
      {notice && notice.tone !== 'success' && (
        <div
          data-testid="position-board-notice"
          data-tone={notice.tone}
          className={`flex items-start justify-between gap-3 px-4 py-3 rounded-lg border ${
            notice.tone === 'success'
              ? 'border-forest/30 bg-forest/10 text-forest'
              : 'border-ember/30 bg-ember/10 text-ember'
          }`}
        >
          <div className="font-mono text-xs leading-relaxed">{notice.text}</div>
          <button
            onClick={() => setNotice(null)}
            className="font-mono text-[10px] uppercase tracking-widest opacity-70 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Position Lifecycle</div>
          <h2 className="font-black text-xl uppercase tracking-wide text-ink">Position Board</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-1.5 font-mono text-xs px-3 py-2 rounded-lg border border-border text-inkMuted hover:text-ink hover:bg-canvas2 transition-colors"
          >
            <Upload size={14} />
            Import Setups
          </button>
          <Link
            to="/positions/planner"
            className="btn-primary flex items-center gap-1.5"
          >
            <Plus size={14} />
            New Position
          </Link>
        </div>
      </div>

      {/* Empty State */}
      {totalCards === 0 && projects.length === 0 && (
        <div className="bg-canvas border border-border rounded-xl px-6 py-16 text-center">
          <div className="font-mono text-xs text-inkMuted mb-3">
            No position projects yet. Use the Position Planner to create your first one.
          </div>
          <Link
            to="/positions/planner"
            className="btn-primary inline-flex items-center gap-1.5"
          >
            <Plus size={14} />
            Open Position Planner
          </Link>
        </div>
      )}

      {/* Board — Stage sections with grid cards */}
      {(totalCards > 0 || projects.length > 0) && (
        <div className="space-y-6">
          {grouped.map(g => (
            <div key={g.stage.id} className="bg-canvas border border-border rounded-xl p-5">
              <StageSection
                stage={g.stage}
                cards={g.cards}
                onCardClick={handleCardClick}
                onDeleteCard={handleDeleteCard}
                onCancelWorking={handleCancelWorking}
                collapsible={true}
              />
            </div>
          ))}
        </div>
      )}

      {/* Import Setups Modal */}
      <SetupImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportComplete={loadProjects}
      />
    </div>
  )
}
